import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchProducts } from "@/lib/api";
import { formatPrice } from "@/lib/whatsapp";
import { useSale, applySale } from "@/lib/sale";

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const sale = useSale();

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      searchProducts(q)
        .then(setResults)
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  if (!open) return null;

  const go = (slug) => {
    onClose();
    navigate(`/product/${slug}`);
  };

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-[60] bg-[#FFFDF8] flex flex-col">
      <div className="max-w-2xl w-full mx-auto px-4 pt-6">
        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-3 bg-white border border-[#ECECEC] rounded-full px-5 min-h-[52px] shadow-sm">
            <Search size={20} className="text-[#9D00FF] shrink-0" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Search idols, master models, STL files..."
              className="flex-1 bg-transparent outline-none text-base text-[#222222] placeholder:text-[#999]"
            />
          </div>
          <button
            onClick={onClose}
            data-testid="search-close-btn"
            aria-label="Close search"
            className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-full bg-white border border-[#ECECEC] text-[#666666]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-6 space-y-2 overflow-y-auto max-h-[70vh] pb-8">
          {loading && <p className="text-sm text-[#666666] px-2">Searching…</p>}
          {!loading && q.length >= 2 && results.length === 0 && (
            <p data-testid="search-no-results" className="text-sm text-[#666666] px-2">
              No products found for “{q}”.
            </p>
          )}
          {results.map((r) => (
            <button
              key={r.slug}
              onClick={() => go(r.slug)}
              data-testid={`search-result-${r.slug}`}
              className="w-full flex items-center gap-4 bg-white border border-[#ECECEC] rounded-2xl p-3 hover:border-[#9D00FF]/40 transition-colors text-left"
            >
              <img src={r.images?.[0]} alt={r.name} className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-sm text-[#222222] truncate">{r.name}</p>
                <p className="text-xs text-[#666666]">
                  {r.has_variants ? "From " : ""}{formatPrice(applySale(r.price, sale).final)}
                  {applySale(r.price, sale).original && (
                    <span className="ml-1.5 line-through text-[#bbb]">{formatPrice(r.price)}</span>
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
